import React, { useState, useEffect, useRef } from 'react';

/**
 * PreviewModal Component
 * Modal for previewing images and PDFs with keyboard shortcuts
 */
export function PreviewModal({
  file,
  fetchPreviewBlob,
  onClose,
}) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  const isImage = file.mimeType.startsWith('image/');
  const isPdf = file.mimeType === 'application/pdf';

  // Fetch preview blob
  useEffect(() => {
    let objectUrl = null;

    const loadPreview = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const blobUrl = await fetchPreviewBlob(file.id);
        objectUrl = blobUrl;
        setPreviewUrl(blobUrl);
      } catch (err) {
        console.error('Error loading preview:', err);
        setError(err.message || 'Failed to load preview');
      } finally {
        setIsLoading(false);
      }
    };

    if (file.previewAvailable) {
      loadPreview();
    } else {
      setError('Preview not available');
      setIsLoading(false);
    }

    // Cleanup
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [file.id, file.previewAvailable, fetchPreviewBlob]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Focus trap
  useEffect(() => {
    // Focus close button on mount
    closeButtonRef.current?.focus();

    // Trap focus within modal
    const handleFocusTrap = (e) => {
      if (!modalRef.current) return;

      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleFocusTrap);
    return () => document.removeEventListener('keydown', handleFocusTrap);
  }, []);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="preview-modal-title"
    >
      <div
        ref={modalRef}
        className="relative w-full max-w-6xl max-h-[90vh] bg-white rounded-lg shadow-xl m-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 id="preview-modal-title" className="text-lg font-semibold text-gray-900 truncate">
            {file.originalName}
          </h2>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close preview"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-auto max-h-[calc(90vh-120px)]">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-gray-600">Loading preview...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="text-4xl mb-3">⚠️</div>
                <p className="text-lg font-medium text-gray-900 mb-2">Preview Unavailable</p>
                <p className="text-sm text-gray-600">{error}</p>
              </div>
            </div>
          )}

          {!isLoading && !error && previewUrl && (
            <>
              {isImage && (
                <div className="flex items-center justify-center">
                  <img
                    src={previewUrl}
                    alt={file.originalName}
                    className="max-w-full max-h-[calc(90vh-200px)] object-contain rounded-lg"
                  />
                </div>
              )}

              {isPdf && (
                <div className="flex flex-col items-center gap-4">
                  <img
                    src={previewUrl}
                    alt={`${file.originalName} - First page preview`}
                    className="max-w-full max-h-[calc(90vh-250px)] object-contain rounded-lg border border-gray-200"
                  />
                  <p className="text-sm text-gray-600">
                    Showing first page preview
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          <p className="text-xs text-gray-500">
            Press <kbd className="px-2 py-1 bg-gray-200 rounded">Esc</kbd> to close
          </p>
        </div>
      </div>
    </div>
  );
}
